'use client';

/**
 * CustomProductsGridCard — the canvas home for the buyer's custom products,
 * laid out as COLLECTIONS (the vendor_catalog idiom).
 *
 * Each category group the assembler returns (Chair, Table, …) is treated as a
 * COLLECTION and painted as a tile (thumbnail = first product image + name +
 * count) in a collections grid — the same shape a vendor's catalogue uses for
 * its design collections. Clicking a collection drills in to that category's
 * product grid with a breadcrumb back; the "Uncategorised" bucket rides along
 * as its own tile (mirrors vendor_catalog's Uncategorized).
 *
 * Drill state is SHARED with the rail: `layout-store.customProductCategory`
 * (null = collections grid, a group key = drilled into it, UNGROUPED = the
 * uncategorised slice). So the rail's Custom Products tree and a canvas
 * collection-tile click drive the same state — one scope, two entry points
 * (mirrors how the vendor tree + the canvas collection grid both pin a vendor).
 *
 * These are the buyer's OWN products (WANTS-owned), so there is no vendor line.
 * A product tile INSPECTS the product (rail brief) without changing the canvas
 * selection — the `custom_products` explorer-selection owns the canvas.
 *
 * @see ../vendors/catalog-card.tsx — the vendor_catalog collections idiom this mirrors
 * @see ../../../explorer/cards/vendors/custom-products-explorer-card.tsx — the rail tree (same scope)
 * @see klyde_edge/ui_config/handlers/catalog.py — catalog_list_custom_products (groups payload)
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo } from 'react';
import { Loader2, Boxes, Grid2X2, Package, ChevronRight } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { CardPlacementProvider, useCardExport } from '@/lib/design/card-export';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { ProductSummary } from '@/lib/api/types';
import { ProductTile } from '../vendors/product-tile';
import { CardGrid } from '../card-grid';
import { CardShell } from '../card-shell';

/** The standard product shape catalog_list_custom_products returns. */
interface CustomProductCard {
  semantic_id: string;
  name: string;
  list_price: number | null;
  image_url: string | null;
  category: string | null;
  saved: boolean;
}
interface CustomProductsResponse {
  groups: { label: string; count: number; products: CustomProductCard[] }[];
  ungrouped: CustomProductCard[];
  count: number;
}

/** A category group rendered as a collection. `key` is what the drill state
 *  (customProductCategory) holds; the uncategorised bucket uses the UNGROUPED
 *  sentinel so it round-trips through the same field. */
interface CustomCollection {
  key: string;
  label: string;
  products: CustomProductCard[];
}

/** Sentinel the rail + this card use for the "Uncategorised" bucket; must match
 *  the explorer card's UNGROUPED. */
const UNGROUPED = '__ungrouped__';

function toSummary(p: CustomProductCard): ProductSummary {
  return {
    semantic_id: p.semantic_id,
    name: p.name,
    list_price: p.list_price ?? null,
    qty_available: null,
    image_url: p.image_url ?? null,
    saved: p.saved,
  };
}

export function CustomProductsGridCard() {
  // Layer-1 chrome (server-driven).
  const title = useProductLabel('custom_products_group');
  const emptyLabel = useProductLabel('custom_products_empty');
  const uncatLabel = useProductLabel('custom_products_uncategorised');

  const strategy = useCapabilityStore((s) => s.strategyName('catalog', 'list_custom_products'));
  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  const inspectedEntity = useGraphSelectionStore((s) => s.inspectedEntity);
  // Shared drill state with the rail tree: null = collections grid, else drilled.
  const activeCategory = useLayoutStore((s) => s.customProductCategory);
  const setCustomProductCategory = useLayoutStore((s) => s.setCustomProductCategory);

  const { data, isLoading } = useStrategyData<CustomProductsResponse>(strategy ?? '', {}, !!strategy);
  const res = data?.data;

  // Fold the payload's groups + ungrouped into one ordered list of collections.
  const collections: CustomCollection[] = useMemo(() => {
    if (!res) return [];
    const cols: CustomCollection[] = res.groups.map((g) => ({
      key: g.label,
      label: g.label,
      products: g.products,
    }));
    if (res.ungrouped.length > 0) {
      cols.push({ key: UNGROUPED, label: uncatLabel, products: res.ungrouped });
    }
    return cols;
  }, [res, uncatLabel]);

  const productTotal = res?.count ?? 0;
  // The drilled-into collection (if the rail / a tile pinned one).
  const drilled = activeCategory ? collections.find((c) => c.key === activeCategory) ?? null : null;

  const queryKey = useMemo(() => ['strategy-data', strategy ?? '', {}] as const, [strategy]);
  useCardExport(useMemo(() => ({
    label: title,
    content_key: 'custom_products_grid',
    surface: 'canvas' as const,
    status: (res ? 'loaded' : 'loading') as 'loading' | 'loaded',
    query_key: queryKey,
    text: drilled
      ? `${title} › ${drilled.label} — ${drilled.products.length} products`
      : `${title} — ${collections.length} collections · ${productTotal} products`,
    json: {
      collections: collections.length,
      count: productTotal,
      drilled_into: drilled?.key ?? null,
    },
    state: { drilled_into: drilled?.key ?? null },
  }), [title, res, queryKey, collections, productTotal, drilled]));

  if (isLoading && !res) {
    return (
      <div style={{ fontSize: surfaceBase.canvas }} className={cn(text.muted, 'flex items-center gap-2 px-3 py-3')}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <div
        style={{ fontSize: surfaceBase.canvas }}
        className="flex flex-col items-center justify-center gap-2 py-20 text-center"
      >
        <Boxes className="h-8 w-8 text-muted-foreground opacity-40" />
        <p className={cn(text.muted)}>{emptyLabel}</p>
      </div>
    );
  }

  // Drill-in — one collection's product grid, breadcrumb back to the collections.
  if (drilled) {
    return (
      <div className="h-full flex flex-col" style={{ fontSize: surfaceBase.canvas }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-card">
          <button
            onClick={() => setCustomProductCategory(null)}
            className={cn(text.body, 'flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors')}
          >
            <Boxes className="h-4 w-4 text-amber-500" />
            {title}
          </button>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
          <span className={cn(text.title, 'capitalize')}>{drilled.label}</span>
          <span className={cn(text.muted, 'ml-auto')}>{drilled.products.length} products</span>
        </div>
        <div className="flex-1 overflow-auto p-4">
          <CardGrid>
            {drilled.products.map((p) => (
              <CardPlacementProvider
                key={p.semantic_id}
                value={{ surface: 'canvas', slot_id: `custom:${p.semantic_id}` }}
              >
                <ProductTile
                  product={toSummary(p)}
                  vendorId=""
                  vendorName=""
                  queryKey={queryKey}
                  collectionName={drilled.label}
                  onClick={() => inspectEntity({ id: p.semantic_id, label: p.name, type: 'product' })}
                  isSelected={inspectedEntity?.id === p.semantic_id}
                />
              </CardPlacementProvider>
            ))}
          </CardGrid>
        </div>
      </div>
    );
  }

  // Top-level — the collections grid (one tile per category group).
  return (
    <div className="h-full flex flex-col" style={{ fontSize: surfaceBase.canvas }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div className={cn(text.heading, 'flex items-center gap-1.5')}>
          <Boxes className="h-4 w-4 text-amber-500" />
          {title}
        </div>
        <p className={text.muted}>
          {collections.length} collections · {productTotal} products
        </p>
      </div>
      <div className="flex-1 overflow-auto p-4">
        <CardGrid>
          {collections.map((c) => (
            <CardPlacementProvider
              key={c.key}
              value={{ surface: 'canvas', slot_id: `custom_collection:${c.key}` }}
            >
              <CustomCollectionTile
                collection={c}
                uncategorised={c.key === UNGROUPED}
                queryKey={queryKey}
                onClick={() => setCustomProductCategory(c.key)}
              />
            </CardPlacementProvider>
          ))}
        </CardGrid>
      </div>
    </div>
  );
}

/** One category group as a collection tile — thumbnail (first product image) +
 *  name + count. Mirrors vendor_catalog's CollectionTile look. */
function CustomCollectionTile({
  collection,
  uncategorised,
  queryKey,
  onClick,
}: {
  collection: CustomCollection;
  uncategorised: boolean;
  queryKey: readonly unknown[];
  onClick: () => void;
}) {
  const thumbnailUrl = collection.products.find((p) => p.image_url)?.image_url ?? null;

  useCardExport(useMemo(() => ({
    label: collection.label,
    content_key: 'custom_collection',
    status: 'loaded' as const,
    query_key: queryKey,
    text: `${collection.label} — ${collection.products.length} product${collection.products.length !== 1 ? 's' : ''}`,
    json: { key: collection.key, label: collection.label, product_count: collection.products.length },
  }), [collection, queryKey]));

  return (
    <CardShell as="button" interactive onClick={onClick} className="group relative overflow-hidden text-left w-full">
      <div className="aspect-square bg-muted relative flex items-center justify-center">
        {thumbnailUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={thumbnailUrl}
            alt={collection.label}
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : uncategorised ? (
          <Package className="w-12 h-12 text-muted-foreground" />
        ) : (
          <Grid2X2 className="w-12 h-12 text-muted-foreground" />
        )}
      </div>
      <div className="p-2">
        <h3 className={cn(text.title, 'capitalize truncate group-hover:text-primary transition-colors')}>
          {collection.label}
        </h3>
        <p className={text.muted}>
          {collection.products.length} product{collection.products.length !== 1 ? 's' : ''}
        </p>
      </div>
    </CardShell>
  );
}
